import { Component, OnInit, Inject, ViewChild, ElementRef, AfterViewInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { RecepcionesService } from '../../Service/recepciones-service';
import { ToastService } from '../../Service/toast-service';

@Component({
  selector: 'app-signature-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule],
  templateUrl: './signature-dialog.html',
  styleUrls: ['./signature-dialog.css']
})
export class SignatureDialog implements OnInit, AfterViewInit {
  @ViewChild('canvasCliente') canvasClienteRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('canvasAsesor') canvasAsesorRef!: ElementRef<HTMLCanvasElement>;

  private ctxCliente!: CanvasRenderingContext2D | null;
  private ctxAsesor!: CanvasRenderingContext2D | null;
  private dibujando = false;

  // 🚀 SIGNALS ORIGINALES PRESERVADAS Y ENLAZADAS
  public clausulas = signal<string[]>([]);
  public terminosAceptados = signal<boolean>(false);
  public procesandoEnvio = signal<boolean>(false);

  public idRecepcion!: number;
  public folioRecepcion: string = '';
  public an8Cliente!: number;
  public etapaFirma: string = 'RECEPCION';

  constructor(
    private recepcionesService: RecepcionesService,
    public toastService: ToastService,
    public dialogRef: MatDialogRef<SignatureDialog>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    // Inicialización segura de parámetros herencia del grid reactivo
    this.idRecepcion = this.data.idRecepcion;
    this.folioRecepcion = this.data.folioRecepcion || '';
    this.an8Cliente = this.data.an8Cliente;
    this.etapaFirma = this.data.etapaFirma || 'RECEPCION';
  }

  ngOnInit(): void {
    console.log(`⚖️ [SaaS Legal] Cargando SignatureDialog para hito: ${this.etapaFirma}`);
    this.cargarClausulasPROFECO();
  }

  ngAfterViewInit(): void {
    this.inicializarLienzoCanvas();
  }

  // 🚀 CONSERVADO: Descarga dinámica multi-tenant de cláusulas contractuales por etapa
  private cargarClausulasPROFECO(): void {
    console.log(`📡 [SaaS Legal] Consultando cláusulas UDC para etapa: ${this.etapaFirma}`);
    
    // Invoca tu método original de negocio para jalar los textos Legales de tu base de datos
    this.recepcionesService.getContratoConfiguracionLegal(this.idRecepcion, this.etapaFirma).subscribe({
    next: (res: any) => {
      if (res && res.clausulasLegales && res.clausulasLegales.length > 0) {
        
        // 🌟 Asignación Segura del Arreglo a la Signal de Cláusulas
        this.clausulas.set(res.clausulasLegales);
        
        // 🌟 Seteo Reactivo de los Atributos de Almacenaje y Garantía
        //this.diasGraciaAlmacenaje.set(res.diasGraciaAlmacenaje || 0);
        //this.costoDiarioAlmacenaje.set(res.costoDiarioAlmacenaje || 0);
        //this.vigenciaGarantia.set(res.vigenciaGarantia || '');

        console.log(`✅ [SaaS Legal] Matriz de ${res.clausulasLegales.length} cláusulas y metadatos comerciales inyectados.`);
      } else {
        this.clausulas.set(['POR DEFECTO: EL CONSUMIDOR ACEPTA LOS TÉRMINOS OPERATIVOS DEL TALLER.']);
      }
    },
    error: (err) => {
      console.error('Error al descargar cláusulas del ERP:', err);
      this.clausulas.set(['ERROR DE RED: NO SE PUDO CONECTAR CON EL CATÁLOGO DE CLÁUSULAS UDC.']);
    }
  });
  }

  public toggleTerminos(): void {
    this.terminosAceptados.set(!this.terminosAceptados());
  }

  private inicializarLienzoCanvas(): void {
    const canvasC = this.canvasClienteRef.nativeElement;
    canvasC.width = 380;
    canvasC.height = 150;
    this.ctxCliente = canvasC.getContext('2d');
    if (this.ctxCliente) {
      this.ctxCliente.strokeStyle = '#0f172a';
      this.ctxCliente.lineWidth = 3;
      this.ctxCliente.lineCap = 'round';
    }
    this.registrarEventosTrazo(canvasC, this.ctxCliente);

    const canvasA = this.canvasAsesorRef.nativeElement;
    canvasA.width = 380;
    canvasA.height = 150;
    this.ctxAsesor = canvasA.getContext('2d');
    if (this.ctxAsesor) {
      this.ctxAsesor.strokeStyle = '#0a58ca';
      this.ctxAsesor.lineWidth = 3;
      this.ctxAsesor.lineCap = 'round';
    }
    this.registrarEventosTrazo(canvasA, this.ctxAsesor);
  }

  private registrarEventosTrazo(canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D | null): void {
    if (!ctx) return;

    const iniciarDibujo = (e: MouseEvent | TouchEvent) => {
      this.dibujando = true;
      const pos = this.obtenerCoordenadasTrazo(e, canvas);
      ctx.beginPath();
      ctx.moveTo(pos.x, pos.y);
    };

    const dibujar = (e: MouseEvent | TouchEvent) => {
      if (!this.dibujando) return;
      e.preventDefault();
      const pos = this.obtenerCoordenadasTrazo(e, canvas);
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
    };

    const detenerDibujo = () => { this.dibujando = false; };

    canvas.addEventListener('mousedown', iniciarDibujo);
    canvas.addEventListener('mousemove', dibujar);
    canvas.addEventListener('mouseup', detenerDibujo);

    canvas.addEventListener('touchstart', (e) => iniciarDibujo(e));
    canvas.addEventListener('touchmove', (e) => dibujar(e));
    canvas.addEventListener('touchend', detenerDibujo);
  }

  private obtenerCoordenadasTrazo(e: MouseEvent | TouchEvent, canvas: HTMLCanvasElement) {
    const rect = canvas.getBoundingClientRect();
    if (e instanceof MouseEvent) {
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    } else {
      const touch = e.touches[0];
      return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
    }
  }

  public limpiarLienzoFisico(panel: 'CLIENTE' | 'ASESOR'): void {
    console.log(`🧹 [SaaS Legal] Limpiando panel de: ${panel}`);
    if (panel === 'CLIENTE' && this.ctxCliente) {
      this.ctxCliente.clearRect(0, 0, 380, 150);
    } else if (panel === 'ASESOR' && this.ctxAsesor) {
      this.ctxAsesor.clearRect(0, 0, 380, 150);
    }
  }

  public procesarYEnviarFirmas(): void {
    this.procesandoEnvio.set(true);

    const base64Cliente = this.canvasClienteRef.nativeElement.toDataURL('image/png');
    const base64Asesor = this.canvasAsesorRef.nativeElement.toDataURL('image/png');

    // 🚀 RECUPERACIÓN DEL COID DEL LOCKER LOCAL
    const userStr = localStorage.getItem('currentUser');
    const coIdSeguro = userStr ? JSON.parse(userStr).coId : 'COMPANY_NOT_FOUND';

    // 🛡️ SOLUCIÓN AL ERROR DE ARGUMENTOS: Formateamos el payload con los campos estrictos y coId
    const payload = {
      coId: coIdSeguro, // Inyectado para satisfacer el contrato del servicio ts(129, 5)
      idRecepcion: Number(this.idRecepcion),
      folioRecepcion: String(this.folioRecepcion),
      firmaClienteBase64: String(base64Cliente),
      firmaAsesorBase64: String(base64Asesor),
      etapaFirma: String(this.etapaFirma)
    };

    console.log('📡 Despachando rúbricas duales formalizadas hacia la API:', payload);

    this.recepcionesService.guardarFirmasConformidad(payload).subscribe({
      next: (res) => {
        this.toastService.showSuccess('Contrato Sellado', `La conformidad de la etapa [${payload.etapaFirma}] fue registrada.`);
        this.dialogRef.close(true);
      },
      error: (err) => {
        this.procesandoEnvio.set(false);
        this.toastService.showDanger('Error de Persistencia', err.error?.error || 'El servidor rechazó el alfiler legal.');
      }
    });
  }

  public cerrarModal(): void {
    this.dialogRef.close(false);
  }
}